/**
 * ColorPickerButton
 * A generic color picker dropdown button that can be used for text color and highlight color
 */
import React from 'react';
import { useSlate } from 'slate-react';
import EditorButton from '../EditorButton/EditorButton';
import { useDropdown } from '../hooks';
import './ColorPickerButton.scss';
import { Editor } from 'slate';

/**
 * Generic ColorPickerButton component
 *
 * @param {Object} props
 * @param {string} props.icon - Material icon name
 * @param {string} props.title - Button tooltip title
 * @param {Array} props.colors - Array of color objects { name, value }
 * @param {Function} props.getColor - Function to get current color from editor
 * @param {Function} props.setColor - Function to set color in editor
 * @param {string} props.removeLabel - Label for remove button (e.g., "Remove color")
 * @param {boolean} props.showIndicator - Whether to always show the color indicator
 * @param {number} props.gridColumns - Number of columns in the color grid (default: 10)
 */

interface Props {
  icon: string;
  title: string;
  colors: { name: string; value: string }[];
  getColor: (editor: Editor) => string | null;
  setColor: (editor: Editor, color: string | null) => void;
  removeLabel?: string;
  showIndicator?: boolean;
  gridColumns?: number;
}

const ColorPickerButton = ({
  icon,
  title,
  colors,
  getColor,
  setColor,
  removeLabel = 'Remove color',
  showIndicator = true,
  gridColumns = 10,
}: Props) => {
  const editor = useSlate();
  const { isOpen, dropdownPos, wrapperRef, buttonRef, toggle, close } = useDropdown();

  const displayColor = getColor(editor);

  const handleColorSelect = (color: string | null) => {
    setColor(editor, color);
    close();
  };

  // Filter out null colors for the grid (shown in remove button instead)
  const gridColors = colors.filter((c) => c.value !== null);

  return (
    <div
      className='color-picker-wrapper'
      ref={wrapperRef}
    >
      <EditorButton
        active={displayColor !== null}
        ref={buttonRef}
        onMouseDown={(e: React.MouseEvent<HTMLSpanElement>) => {
          e.preventDefault();
          toggle(e as unknown as MouseEvent);
        }}
        title={title}
      >
        <span className='editor-icon material-icons'>{icon}</span>
        {(showIndicator || displayColor) && (
          <div
            className='color-button-indicator'
            style={{ backgroundColor: displayColor || '#000000' }}
          />
        )}
      </EditorButton>
      {isOpen && (
        <div
          className='color-dropdown'
          style={{ top: dropdownPos.y, left: dropdownPos.x }}
        >
          <div
            className='color-palette-grid'
            style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}
          >
            {gridColors.map((colorObj) => {
              const swatchClassName = [
                'color-swatch',
                displayColor === colorObj.value ? ' selected' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  type='button'
                  key={colorObj.value}
                  className={swatchClassName}
                  style={{ backgroundColor: colorObj.value }}
                  title={colorObj.name}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleColorSelect(colorObj.value);
                  }}
                />
              );
            })}
          </div>
          <button
            type='button'
            className='color-remove-button'
            onMouseDown={(e) => {
              e.preventDefault();
              handleColorSelect(null);
            }}
          >
            <span
              className='material-icons'
              style={{ fontSize: 14 }}
            >
              format_color_reset
            </span>
            {removeLabel}
          </button>
        </div>
      )}
    </div>
  );
};

ColorPickerButton.displayName = 'ColorPickerButton';

export default ColorPickerButton;
